import dayjs from '~/timeInit';

import { LuxPlusScheduleDefault, LuxPlusScheduleDefaultUpdate } from '~/services/OffersService';

export interface CellProvenance {
  updatedBy: string;
  updatedAt: string;
}

export const KEY_SEPARATOR = '|';

export function buildCellKey(region: string, tier: string): string {
  return `${region}${KEY_SEPARATOR}${tier}`;
}

/** Splits on the first separator only, so a tier containing one still round-trips. */
export function splitCellKey(key: string): { region: string; tier: string } {
  const separatorIndex = key.indexOf(KEY_SEPARATOR);
  return { region: key.slice(0, separatorIndex), tier: key.slice(separatorIndex + 1) };
}

export function keysForRegion(region: string, tiers: ReadonlyArray<string>): Array<string> {
  return tiers.map((tier) => buildCellKey(region, tier));
}

export function keysForTier(tier: string, regions: ReadonlyArray<string>): Array<string> {
  return regions.map((region) => buildCellKey(region, tier));
}

export function keysForAll(regions: ReadonlyArray<string>, tiers: ReadonlyArray<string>): Array<string> {
  return regions.flatMap((region) => keysForRegion(region, tiers));
}

export function countEnabled(keys: Array<string>, enabled: Record<string, boolean>): number {
  return keys.filter((key) => enabled[key]).length;
}

export function shortTierLabels(tiers: ReadonlyArray<string>): Array<string> {
  if (tiers.length < 2) {
    return [...tiers];
  }

  const [first, ...rest] = tiers;
  let shared = 0;

  while (shared < first.length && rest.every((tier) => tier[shared] === first[shared])) {
    shared += 1;
  }

  // Drop every shared word EXCEPT the last one, which stays as context. Cutting at the end of the
  // shared run instead would turn "Luxury Escapes - Tier 1" into a bare "1".
  const boundary = first.slice(0, shared).trimEnd().lastIndexOf(' ');
  if (boundary <= 0) {
    return [...tiers];
  }

  return tiers.map((tier) => tier.slice(boundary + 1));
}

export function savedFromRows(rows: Array<LuxPlusScheduleDefault> = []): Record<string, boolean> {
  const saved: Record<string, boolean> = {};
  rows.forEach((row) => {
    saved[buildCellKey(row.region, row.tier)] = row.enabled;
  });
  return saved;
}

export function provenanceFromRows(rows: Array<LuxPlusScheduleDefault> = []): Record<string, CellProvenance> {
  const provenance: Record<string, CellProvenance> = {};
  rows.forEach((row) => {
    provenance[buildCellKey(row.region, row.tier)] = { updatedBy: row.updatedBy, updatedAt: row.updatedAt };
  });
  return provenance;
}

export function provenanceLabel(cell: CellProvenance | undefined): string {
  if (!cell) {
    return 'Never configured';
  }

  return `Last changed by ${cell.updatedBy} on ${dayjs(cell.updatedAt).format('D MMM YYYY, HH:mm')}`;
}

export function dirtyKeysFrom(changes: Record<string, boolean>, saved: Record<string, boolean>): Array<string> {
  return Object.keys(changes).filter((key) => !!changes[key] !== !!saved[key]);
}

export function buildUpdates(
  dirtyKeys: Array<string>,
  changes: Record<string, boolean>,
  brand: string,
): Array<LuxPlusScheduleDefaultUpdate> {
  return dirtyKeys.map((key) => {
    const { region, tier } = splitCellKey(key);
    return { brand, region, tier, enabled: !!changes[key] };
  });
}

const MAX_ERROR_DETAIL = 3;

// A 400 arrives as a bare "validation error"; the region or tier at fault is only in `errors`.
export function describeApiError(error: unknown): string {
  const { message, errors } = (error ?? {}) as { message?: string; errors?: unknown };
  const base = message ?? 'Unknown error';

  if (!Array.isArray(errors) || errors.length === 0) {
    return base;
  }

  const details = errors.slice(0, MAX_ERROR_DETAIL).map((entry) => {
    if (typeof entry === 'string') {
      return entry;
    }

    const { message: entryMessage } = (entry ?? {}) as { message?: string };
    return entryMessage ?? 'unspecified';
  });

  const remaining = errors.length - details.length;
  const suffix = remaining > 0 ? `, and ${remaining} more` : '';

  return `${base} (${details.join('; ')}${suffix})`;
}
